import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { Options as RRuleOptions } from 'rrule';
import { ClearButton } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { RepeatText } from './RepeatText';

export function RepeatTextButton({
  onPress,
  rruleOptions,
  isConfiguring,
  onClear,
  disabled,
  offset,
  tagFilter,
  workflowTemplateId,
}: {
  onPress: () => void;
  isConfiguring: boolean;
  disabled: boolean;
  rruleOptions?: Partial<RRuleOptions>;
  onClear: () => void;
  offset?: API.CreateRecurrenceOffset | null;
  tagFilter?: API.FilterByComponentFragment;
  workflowTemplateId?: string;
}) {
  const colors = useContext(ColorThemeContext);
  const hasRruleOptions = !isNil(rruleOptions);
  const isDesktop = useIsDesktop();
  const showClearButton = !isNil(onClear) && hasRruleOptions;

  // Validate offset is fully configured before showing as repeat config
  const hasValidTagFilter =
    !isNil(tagFilter?.componentId) && !isEmpty(tagFilter?.value?.containsString?.values);
  const hasValidOffset = !isNil(offset?.unit) && !isNil(offset?.value) && offset.value > 0;
  const isValidOneAtATime = hasValidTagFilter && hasValidOffset;

  const hasRepeatConfig = hasRruleOptions || isValidOneAtATime;

  return (
    <View
      row
      centerVertically
      paddingLeft={isDesktop ? 1.5 : 2}
      paddingRight={1}
      paddingBottom={1}
      onClick={disabled ? undefined : onPress}
      style={{
        minHeight: 60,
        backgroundColor: 'transparent',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <View marginRight={1.5}>
        <Icon name="repeat" gridSize={4.375} color={colors.accent} />
      </View>
      {!hasRepeatConfig && !isConfiguring && (
        <Text size="body1">{i18n('workflowTemplate.component.dateAndRepeat.addRepeat')}</Text>
      )}
      {hasRepeatConfig && (
        <View flex paddingVertical={1}>
          {!isDesktop && (
            <Text size="overline" inactive>
              {i18n('workflowTemplate.component.dateAndRepeat.repeat')}
            </Text>
          )}
          <RepeatText
            rruleOptions={rruleOptions}
            offset={offset}
            tagFilter={tagFilter}
            workflowTemplateId={workflowTemplateId}
          />
        </View>
      )}
      {showClearButton && (
        <View stopPropagation>
          <ClearButton onClick={onClear} />
        </View>
      )}
      {isConfiguring && !hasRepeatConfig && (
        <Text size="body2" italic inactive>
          {i18n('workflowTemplate.component.dateAndRepeat.customRepeat')}
        </Text>
      )}
    </View>
  );
}
